import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LabelLoadingBox from 'LuxKit/Label/LabelLoadingBox'
import { rem } from 'polished'
import type { HTMLAttributes } from 'react'
import { memo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextButtonLoadingSkeleton from 'LuxKit/Button/TextButtonLoadingSkeleton'

// Mirrors GrowthCarouselCardSmall rather than the control skeleton's unframed layout.
// Keep the same image ratio and content spacing while the offer data is loading.
const Container = styled.div<{ $mobileWidth: '75vw' | '80vw' }>`
  display: grid;
  grid-template-rows: auto 1fr;
  width: ${props => props.$mobileWidth};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.growth-tile-no-border {
    border-color: transparent;
    background-clip: padding-box;
  }
  border-radius: ${rem(12)};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  > :first-child {
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(320)};
  }
`

const Details = styled(Group)`
  padding: ${rem(16)};
  min-width: 0;
`

interface Props extends HTMLAttributes<HTMLDivElement> {
  locked?: boolean
  mobileWidth?: '75vw' | '80vw'
}

function GrowthCarouselCardLoadingSkeleton({ locked = false, mobileWidth = '80vw', className, ...rest }: Props) {
  return <Container {...rest} $mobileWidth={mobileWidth} className={clsx(className, 'size-small', 'growth-tile-skeleton')}>
    <LoadingBox />
    <Details direction="vertical" gap={12}>
      {locked ? <LabelLoadingBox width={200} /> : <Group direction="horizontal" horizontalAlign="space-between">
        <TextLoadingBox typography="caption-medium" width={180} />
        <LoadingBox width={28} height={24} />
      </Group>}
      <Group direction="vertical" gap={4}>
        {!locked && <TextLoadingBox typography="body-medium" width="80%" />}
        <TextLoadingBox typography="body-medium" width="100%" lines={2} />
      </Group>
      <Group direction="vertical" gap={4}>
        <TextLoadingBox typography="caption-large" width="95%" />
        <TextLoadingBox typography="caption-large" width="85%" />
        <TextLoadingBox typography="caption-large" width="90%" />
        <TextLoadingBox typography="caption-large" width="25%" />
      </Group>
      {locked ? <LoadingBox height={40} /> : <>
        <LabelLoadingBox width={180} />
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <Group direction="vertical" gap={4}>
            <TextLoadingBox typography="caption-medium" width={90} />
            <TextLoadingBox typography="heading5" width={100} />
            <TextLoadingBox typography="caption-medium" width={120} />
          </Group>
          <CSSBreakpoint min="desktop">
            <TextButtonLoadingSkeleton width="10ch" />
          </CSSBreakpoint>
        </Group>
      </>}
    </Details>
  </Container>
}

export default memo(GrowthCarouselCardLoadingSkeleton)
